/**
 * KnowledgeZone — vibe-designer 2026-06-29 Knowledge redesign.
 *
 * A labeled section card that gives the Knowledge page its information
 * hierarchy: each of the three zones (Add → Ask/Search → Status) is one of
 * these, with a colored icon chip + title + one-line description so the user
 * can tell "act here" from "read here" at a glance. The card supplies the
 * elevated surface + visible border the page was missing; the panels rendered
 * as `children` are bare (no card shell of their own) so there is no
 * double-border nesting.
 */
import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

interface KnowledgeZoneProps {
  /** Lucide icon shown in the accent chip. */
  icon: LucideIcon;
  /** Zone title, e.g. "Add knowledge". */
  title: string;
  /** One-line description under the title. */
  description: string;
  /** `text-*` accent class for the icon chip + title underline. */
  accentTextClass: string;
  /** `bg-*` accent class (tinted at /10 for the chip background). */
  accentBgClass: string;
  testid: string;
  /** Right-aligned header slot (e.g. refresh controls). */
  headerRight?: ReactNode;
  children: ReactNode;
}

export function KnowledgeZone({
  icon: Icon,
  title,
  description,
  accentTextClass,
  accentBgClass,
  testid,
  headerRight,
  children,
}: KnowledgeZoneProps) {
  return (
    <section
      data-testid={testid}
      className="rounded-xl border border-vibe-border-strong bg-vibe-surface shadow-vibe-md mb-5 overflow-hidden"
    >
      <header className="flex items-center gap-3 border-b border-vibe-border px-4 py-3">
        <span
          className={clsx(
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg',
            accentBgClass,
            accentTextClass,
          )}
        >
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className={clsx('text-base font-semibold leading-tight text-vibe-text')}>
            {title}
          </h2>
          <p className="text-xs text-vibe-text-secondary leading-tight mt-0.5">
            {description}
          </p>
        </div>
        {headerRight && <div className="flex-shrink-0">{headerRight}</div>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}
